/**
 * OE Conditions panel (Beta-1 plan P3-01 · Tim C0820, D06.1 "Listed in OE Conditions: FLS /
 * Dept Head / Site Manager / All Sites"): the scoped list that sits at the bottom of every
 * dashboard and is the `oe-conditions` widget. Rows show ID · title · type/subtype · severity ·
 * status · owner · age, with quick filters (status, type, "mine", "needs my action") and an
 * "action required" badge; a row opens the detail drawer (P2-08 review view, resolve, hide).
 *
 * Data comes from the caller (`items`) — the role dashboards pass `useTRLs()` (persona-scoped),
 * the OE Dashboard passes the scope-filtered list — so this component opens no listener itself.
 */
import React, { useMemo, useState } from 'react';
import { Box, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography, Badge, Tooltip, Button, TextField, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { CONDITION_STATUS_ORDER, conditionStatusLabel, conditionStatusColor, conditionTypeLabel, conditionSubtypeLabel, resolvedSubtitle, CONDITION_TYPES, HIDDEN_FROM_QUEUES } from '../utils/trlConstants';
import { needsL1Action, needsL2Action, canApproveResolution, canResolve, isHiddenForViewer } from '../utils/conditionWorkflow';
import { sevColor } from './conditions/ConditionHeader';
import { ConditionDetailDrawer } from './conditions/ConditionDetailDrawer';
import { useConditionViewer } from '../hooks/useConditionViewer';
import { useDemoRole } from '../../../contexts/DemoRoleContext';
import { canAccessToolPath } from '../utils/accessControl';

const ENTRY = '/drew/rollup/trl-entry';
const L1 = '/drew/rollup/review-queue';
const L2 = '/drew/rollup/severity-calibration';

function ageOf(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day';
  if (days < 30) return `${days} days`;
  return `${Math.floor(days / 30)} mo`;
}

/** What THIS viewer must do about a condition, if anything (drives the badge + the link). */
export function actionFor(c, viewer) {
  if (!viewer) return null;
  if (needsL1Action(c, viewer, viewer.routing)) return { label: c.status === 'resolution_pending' ? 'Approve resolution (L1)' : 'L1 review', href: L1 };
  if (needsL2Action(c, viewer, viewer.routing)) return { label: c.status === 'resolution_pending' ? 'Approve resolution (L2)' : 'L2 approval', href: L2 };
  if (c.status === 'approved' && canResolve(c, viewer) && (c.ownerUid === viewer.uid || (!c.ownerUid && c.ownerDeptId && c.ownerDeptId === viewer.deptId))) return { label: 'Resolve', href: null };
  if (c.status === 'rejected' && c.submittedBy === viewer.uid) return { label: 'Not approved', href: ENTRY };
  return null;
}

export function OEConditionsPanel({
  items = [],
  title = 'OE Conditions',
  scopeLabel = '',
  compact = false,
  maxRows = compact ? 6 : 50,
  defaultFilter = 'open',
  showTitle = true,
  onOpenAll,
}) {
  const navigate = useNavigate();
  const viewer = useConditionViewer();
  const { personaId } = useDemoRole();
  const [status, setStatus] = useState(defaultFilter);
  const [type, setType] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  // Always the LIVE record (the list is a subscription), so the drawer reflects changes as they land
  const selected = selectedId ? items.find((c) => c.id === selectedId) || null : null;
  const setSelected = (c) => setSelectedId(c ? c.id : null);

  const visible = useMemo(() => items.filter((c) => !isHiddenForViewer(c, viewer)), [items, viewer]);
  // The badge links to a queue page; never offer it to a lens the access matrix would bounce
  // (e.g. Department Head → L1/FLS Review Queue shows "Not available at this access level").
  const withAction = useMemo(
    () => visible.map((c) => {
      const action = actionFor(c, viewer);
      return { c, action: action && action.href && !canAccessToolPath(personaId, action.href) ? null : action };
    }),
    [visible, viewer, personaId]
  );
  const needsMe = withAction.filter((x) => x.action).length;

  const rows = withAction
    .filter(({ c, action }) => {
      if (status === 'open') return !['resolved', 'rejected', ...HIDDEN_FROM_QUEUES].includes(c.status);
      if (status === 'action') return Boolean(action);
      if (status === 'mine') return c.submittedBy === viewer.uid || c.ownerUid === viewer.uid;
      if (status === 'closed') return ['resolved', 'rejected'].includes(c.status);
      if (status === 'all') return true;
      return c.status === status;
    })
    .filter(({ c }) => type === 'all' || c.type === type)
    .slice(0, maxRows);

  const counts = { open: 0, action: needsMe, mine: 0, closed: 0, all: visible.length };
  for (const { c } of withAction) {
    if (!['resolved', 'rejected', ...HIDDEN_FROM_QUEUES].includes(c.status)) counts.open += 1;
    if (c.submittedBy === viewer.uid || c.ownerUid === viewer.uid) counts.mine += 1;
    if (['resolved', 'rejected'].includes(c.status)) counts.closed += 1;
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {showTitle && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} useFlexGap flexWrap="wrap">
          <Badge badgeContent={needsMe} color="error" invisible={!needsMe}>
            <Typography variant="h6" sx={{ pr: needsMe ? 1 : 0 }}>
              {title}
            </Typography>
          </Badge>
          {scopeLabel && (
            <Typography variant="body2" color="text.secondary">
              · {scopeLabel}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {onOpenAll && (
            <Button size="small" onClick={onOpenAll}>
              View all
            </Button>
          )}
        </Stack>
      )}
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => v && setStatus(v)} aria-label="Status filter">
          <ToggleButton value="open">Open ({counts.open})</ToggleButton>
          <ToggleButton value="action">Needs my action ({counts.action})</ToggleButton>
          <ToggleButton value="mine">Mine ({counts.mine})</ToggleButton>
          <ToggleButton value="closed">Closed ({counts.closed})</ToggleButton>
          <ToggleButton value="all">All ({counts.all})</ToggleButton>
        </ToggleButtonGroup>
        {!compact && (
          <TextField select size="small" label="Type" value={type} onChange={(e) => setType(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="all">All types</MenuItem>
            {CONDITION_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
        )}
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {rows.length === 0 ? (
          <Box sx={{ minHeight: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.75, color: 'text.secondary', textAlign: 'center', py: 3 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 30, opacity: 0.5 }} />
            <Typography variant="body2">No OE Conditions match this filter.</Typography>
          </Box>
        ) : (
          <Table size="small" stickyHeader sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell sx={{ whiteSpace: 'normal !important', minWidth: 200 }}>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
                {!compact && <TableCell>Owner</TableCell>}
                <TableCell>Age</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ c, action }) => {
                const sev = c.severityDH || c.severityFLS || c.severityITM;
                const ownerDept = (viewer.org?.departments || []).find((d) => d.id === c.ownerDeptId);
                return (
                  <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelected(c)}>
                    <TableCell sx={{ fontFamily: '"Roboto Mono", monospace', fontWeight: 700 }}>{c.conditionId || `#${c.id.slice(0, 6)}`}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal !important' }}>
                      <Typography variant="body2" fontWeight={600} noWrap={compact}>
                        {c.title}
                      </Typography>
                      {!compact && conditionSubtypeLabel(c) && (
                        <Typography variant="caption" color="text.secondary">
                          {conditionSubtypeLabel(c)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{conditionTypeLabel(c.type)}</TableCell>
                    <TableCell>{sev && <Chip label={sev} size="small" sx={{ bgcolor: sevColor(sev), color: '#fff', fontWeight: 700, minWidth: 34 }} />}</TableCell>
                    <TableCell>
                      <Chip label={resolvedSubtitle(c) ? `${conditionStatusLabel(c.status)} · ${resolvedSubtitle(c)}` : conditionStatusLabel(c.status)} size="small" color={conditionStatusColor(c.status)} sx={{ fontWeight: 600 }} />
                    </TableCell>
                    {!compact && <TableCell>{c.ownerName || ownerDept?.name || '—'}</TableCell>}
                    <TableCell>{ageOf(c.submittedAt)}</TableCell>
                    <TableCell align="right">
                      {action && (
                        <Tooltip title={action.label}>
                          <Chip
                            size="small"
                            color="error"
                            variant="outlined"
                            label={action.label}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (action.href) navigate(action.href);
                              else setSelected(c);
                            }}
                            sx={{ fontWeight: 700 }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>
      <ConditionDetailDrawer condition={selected} viewer={viewer} open={Boolean(selected)} onClose={() => setSelected(null)} />
    </Box>
  );
}

export default OEConditionsPanel;
